import { useModalAction, useModalState } from "@/contexts/modal/Context.tsx";
import CalendarIllustration from "@/components/ui/CalendarIllustration.tsx";
import dayjs from "dayjs";
import routes from "@/config/routes.ts";
import { useMe } from "@/data/user.ts";
import { Button } from "@/components/ui/Button.tsx";
import { useBookOneToOneMutation } from "@/data/one-to-one.ts";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
interface Props {
  tutor: string;
  date: string;
  hour: string;
}
export default function ConfirmOneToOneFirstSessionModal() {
  const { data } = useModalState();
  const { date, tutor, hour } = data as Props;
  const { closeModal } = useModalAction();
  const { me } = useMe();
  const { mutate, isLoading } = useBookOneToOneMutation(closeModal);

  const { t } = useTranslation();

  const navigate = useNavigate();

  const newDate = dayjs(date);

  const [step, setStep] = useState<number>(0);

  const handleConfirm = () => {
    if (!me) return;

    mutate({
      tutor_token: tutor,
      user_token: me.item.user.token,
      data: newDate.format("YYYY-MM-DD"),
      ora: hour,
      ora_fine: dayjs(newDate.format("YYYY-MM-DD") + " " + hour)
        .add(1, "hour")
        .format("HH:mm"),
      titolo_argomento: "General topic",
      descrizione: "General topic",
      id_material: 751,
    });

    setStep(1);
  };

  return step === 0 ? (
    <div>
      <header className={"flex flex-col items-center"}>
        <p className={"!text-secondary text-2xl font-semibold"}>
          {t("one_to_one.confirm_first_session_modal.title")}
        </p>
        <p className="text-14px text-primary">
          {/* An event will be created in your&nbsp;
          <b className="text-secondary">Agenda</b> */}
          {t("one_to_one.confirm_first_session_modal.subtitle")}
        </p>
      </header>
      <div className={"flex flex-col justify-center h-full gap-y-10 mb-5"}>
        <div className=" flex flex-row items-center justify-center w-full gap-10">
          <CalendarIllustration
            className="w-[135px] h-[135px] rounded-md"
            header={dayjs(date).format("ddd")}
            day={dayjs(date).format("DD")}
            month={dayjs(date).format("MMM")}
          />
          <div className="flex flex-col items-start gap-4">
            <div className={" gap-0 flex-col items-start"}>
              <h5 className={"font-semibold"}>
                {/* Time */}
                {t("one_to_one.confirm_first_session_modal.time.title")}
              </h5>
              <p className={"text-3xl font-semibold text-brand-secondary"}>
                {hour}
              </p>
            </div>
            <div className={"detail gap-0  flex-col items-start large"}>
              <h5 className={"font-semibold"}>
                {/* Duration */}
                {t("one_to_one.confirm_first_session_modal.duration.title")}
              </h5>
              <p className={"text-3xl font-semibold text-brand-secondary"}>
                {/* 1 hour */}
                {t("one_to_one.confirm_first_session_modal.duration.info")}
              </p>
            </div>
          </div>
        </div>

        <footer className={"mb-6"}>
          <div className=" w-full justify-center flex flex-row gap-6">
            <Button
              variant={"outline"}
              onClick={() => {
                closeModal();
              }}
            >
              {/* Back */}
              {t("btns.back")}
            </Button>
            <Button
              onClick={() => {
                if (!date && !hour) return;
                handleConfirm();
              }}
              disabled={isLoading}
              isLoading={isLoading}
            >
              {/* Confirm */}
              {t("btns.confirm")}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  ) : (
    <div>
      <header className={"flex flex-col items-center"}>
        <p className={"!text-secondary text-2xl font-semibold"}>
          Great! First lesson booked!
        </p>
        <p className="text-14px text-primary">
          You can find the event for the {dayjs(date).format("DD/MM/YYYY")} at{" "}
          {hour} in your&nbsp;
          <b className="text-secondary">Agenda</b>
        </p>
      </header>
      <div
        className={
          "flex flex-col justify-center items-center gap-6 h-full mb-5"
        }
      >
        <svg width="39" height="39" viewBox="0 0 39 39" fill="none">
          <path
            d="M36.5381 15.116C35.8817 14.43 35.2027 13.7231 34.9468 13.1016C34.71 12.5322 34.6961 11.5886 34.6821 10.6745C34.656 8.97522 34.6282 7.0496 33.2893 5.71071C31.9504 4.37183 30.0248 4.34397 28.3255 4.31786C27.4114 4.30393 26.4678 4.29 25.8984 4.05321C25.2786 3.79728 24.57 3.11826 23.884 2.46187C22.6827 1.30754 21.3177 0 19.5 0C17.6823 0 16.3191 1.30754 15.116 2.46187C14.43 3.11826 13.7231 3.79728 13.1016 4.05321C12.5357 4.29 11.5886 4.30393 10.6745 4.31786C8.97522 4.34397 7.0496 4.37183 5.71071 5.71071C4.37183 7.0496 4.35268 8.97522 4.31786 10.6745C4.30393 11.5886 4.29 12.5322 4.05321 13.1016C3.79728 13.7214 3.11826 14.43 2.46187 15.116C1.30754 16.3173 0 17.6823 0 19.5C0 21.3177 1.30754 22.6809 2.46187 23.884C3.11826 24.57 3.79728 25.2769 4.05321 25.8984C4.29 26.4678 4.30393 27.4114 4.31786 28.3255C4.34397 30.0248 4.37183 31.9504 5.71071 33.2893C7.0496 34.6282 8.97522 34.656 10.6745 34.6821C11.5886 34.6961 12.5322 34.71 13.1016 34.9468C13.7214 35.2027 14.43 35.8817 15.116 36.5381C16.3173 37.6925 17.6823 39 19.5 39C21.3177 39 22.6809 37.6925 23.884 36.5381C24.57 35.8817 25.2769 35.2027 25.8984 34.9468C26.4678 34.71 27.4114 34.6961 28.3255 34.6821C30.0248 34.656 31.9504 34.6282 33.2893 33.2893C34.6282 31.9504 34.656 30.0248 34.6821 28.3255C34.6961 27.4114 34.71 26.4678 34.9468 25.8984C35.2027 25.2786 35.8817 24.57 36.5381 23.884C37.6925 22.6827 39 21.3177 39 19.5C39 17.6823 37.6925 16.3191 36.5381 15.116ZM34.5272 21.9567C33.6932 22.8272 32.8296 23.7273 32.3717 24.8329C31.933 25.895 31.9138 27.1085 31.8964 28.2837C31.879 29.5025 31.8599 30.7787 31.3184 31.3184C30.7769 31.8581 29.5094 31.879 28.2837 31.8964C27.1085 31.9138 25.895 31.933 24.8329 32.3717C23.7273 32.8296 22.8272 33.6932 21.9567 34.5272C21.0861 35.3612 20.1964 36.2143 19.5 36.2143C18.8036 36.2143 17.9069 35.3577 17.0433 34.5272C16.1798 33.6967 15.2727 32.8296 14.1671 32.3717C13.105 31.933 11.8915 31.9138 10.7163 31.8964C9.49755 31.879 8.22134 31.8599 7.68161 31.3184C7.14188 30.7769 7.12098 29.5094 7.10357 28.2837C7.08616 27.1085 7.06701 25.895 6.62826 24.8329C6.17036 23.7273 5.30679 22.8272 4.47281 21.9567C3.63884 21.0861 2.78571 20.1964 2.78571 19.5C2.78571 18.8036 3.64232 17.9069 4.47281 17.0433C5.3033 16.1798 6.17036 15.2727 6.62826 14.1671C7.06701 13.105 7.08616 11.8915 7.10357 10.7163C7.12098 9.49755 7.14013 8.22134 7.68161 7.68161C8.22308 7.14188 9.49058 7.12098 10.7163 7.10357C11.8915 7.08616 13.105 7.06701 14.1671 6.62826C15.2727 6.17036 16.1728 5.30679 17.0433 4.47281C17.9139 3.63884 18.8036 2.78571 19.5 2.78571C20.1964 2.78571 21.0931 3.64232 21.9567 4.47281C22.8202 5.3033 23.7273 6.17036 24.8329 6.62826C25.895 7.06701 27.1085 7.08616 28.2837 7.10357C29.5025 7.12098 30.7787 7.14013 31.3184 7.68161C31.8581 8.22308 31.879 9.49058 31.8964 10.7163C31.9138 11.8915 31.933 13.105 32.3717 14.1671C32.8296 15.2727 33.6932 16.1728 34.5272 17.0433C35.3612 17.9139 36.2143 18.8036 36.2143 19.5C36.2143 20.1964 35.3577 21.0931 34.5272 21.9567ZM18.1071 20.8929V11.1429C18.1071 10.7734 18.2539 10.4192 18.5151 10.158C18.7763 9.89675 19.1306 9.75 19.5 9.75C19.8694 9.75 20.2237 9.89675 20.4849 10.158C20.7461 10.4192 20.8929 10.7734 20.8929 11.1429V20.8929C20.8929 21.2623 20.7461 21.6165 20.4849 21.8778C20.2237 22.139 19.8694 22.2857 19.5 22.2857C19.1306 22.2857 18.7763 22.139 18.5151 21.8778C18.2539 21.6165 18.1071 21.2623 18.1071 20.8929ZM21.5893 27.1607C21.5893 27.5739 21.4668 27.9779 21.2372 28.3215C21.0076 28.665 20.6813 28.9328 20.2995 29.091C19.9178 29.2491 19.4977 29.2905 19.0924 29.2099C18.6871 29.1292 18.3148 28.9303 18.0227 28.6381C17.7305 28.3459 17.5315 27.9736 17.4509 27.5683C17.3702 27.163 17.4116 26.7429 17.5698 26.3612C17.7279 25.9794 17.9957 25.6531 18.3393 25.4235C18.6828 25.194 19.0868 25.0714 19.5 25.0714C20.0541 25.0714 20.5855 25.2916 20.9773 25.6834C21.3692 26.0752 21.5893 26.6066 21.5893 27.1607Z"
            fill="#8065C9"
          />
        </svg>
        <div
          className={
            "border-[1px] border-solid w-[455px] border-[--secondary] rounded-md h-[35px] bg-white flex flex-row items-center justify-center text-sm"
          }
        >
          This will be an introductory Lesson with&nbsp;
          <b className={"text-[--secondary] font-semibold"}>GENERAL TOPIC</b>
        </div>
        <div
          className={
            "w-full items-center  flex-col flex gap-3 leading-[100%] h-[54px] text-center text-sm text-[--secondary]"
          }
        >
          <p className={"font-normal"}>
            You will meet your teacher that follow you in your path
          </p>
          <p className={"font-semibold"}>
            From the next lesson you will be able to choose among various topics
          </p>
        </div>

        <footer className={"mb-6"}>
          <div className=" w-full justify-center flex flex-row gap-6">
            <Button
              onClick={() => {
                navigate(routes.agenda);
                closeModal();
              }}
              disabled={isLoading}
              isLoading={isLoading}
            >
              Go to agenda
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}
